import "core-js";
import "utils/remove";
import "style/css/bulma.css";
import "style/css/bulma.dropdown.css";
import "style/css/bulma.icon.css";
import "style/css/bulma.popover.css";
import "@adfenix/alexandria/dist/style.css";

import * as serviceWorker from "./serviceWorker";

import {
  QueryCache,
  QueryClient,
  QueryClientProvider
} from "@tanstack/react-query";

import { ADFENIX_GTM_ID } from "utils/constants";
import { Alexandria } from "@adfenix/alexandria";
import { AppRoutes } from "routes";
import { ErrorContent } from "api/http";
import { RecoilRoot } from "recoil";
import TagManager from "react-gtm-module";
import amplify from "lib/amplify/amplify";
import { createRoot } from "react-dom/client";
import {
  isAmplifyConfigExists,
  getAmplifyConfigFromStorage
} from "api/ssoHelper";
import { ProSidebarProvider } from "react-pro-sidebar";
import { toast } from "react-toastify";

async function run() {
  // The mocks are only imported during development but webpack will still bundle them.
  // With a conditional import webpack will put the mocks in separate chunks.
  // By providing webpackChunkName the mock files are named and can be removed after the build.
  // See mock:cleanup in package.json.
  if (
    process.env.NODE_ENV === "development" &&
    process.env.REACT_APP_API !== "production"
  ) {
    /* eslint-disable @typescript-eslint/no-var-requires */
    const { worker } = await import(
      /* webpackChunkName: "mocks" */ "./mocks/browser"
    );

    await worker.start({ onUnhandledRequest: "bypass" });
  }

  const tagManagerArgs = {
    gtmId: ADFENIX_GTM_ID
  };

  if (process.env.NODE_ENV === "production") {
    TagManager.initialize(tagManagerArgs);
  }

  if (isAmplifyConfigExists()) {
    amplify(getAmplifyConfigFromStorage());
  }

  const domNode = document.getElementById("root");

  if (!domNode) {
    throw new Error("Root not found");
  }
  const root = createRoot(domNode);
  const queryClient = new QueryClient({
    queryCache: new QueryCache({
      onError: (_, query) => {
        if (typeof query.meta?.errorMessage === "string") {
          toast.error(query.meta.errorMessage);
        }
      },
      onSuccess: (_, query) => {
        if (typeof query.meta?.successMessage === "string") {
          toast.success(query.meta.successMessage);
        }
      }
    }),
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        refetchOnReconnect: false,
        enabled: true,
        retry: (count, error) => {
          return (error as ErrorContent)?.status !== 404 && count < 3;
        },
        retryDelay: attempt =>
          Math.min(attempt > 1 ? 2 ** attempt * 5000 : 1000, 30 * 1000)
      },
      mutations: {
        retry: 0
      }
    }
  });

  if (window.location.protocol === "file:") {
    root.render(
      <div className="file-protocol-notice">
        We do not support saved html pages.
        <br />
        <span>
          Please use the Adfenix{" "}
          <a href="https://app.adfenix.com/login">portal.</a>
        </span>
      </div>
    );
  } else {
    root.render(
      <QueryClientProvider client={queryClient}>
        <RecoilRoot>
          <ThemeWrapper />
        </RecoilRoot>
      </QueryClientProvider>
    );

    if (process.env.NODE_ENV !== "development") {
      serviceWorker.unregister();
    }
  }
}

const ThemeWrapper = () => {
  return (
    <Alexandria>
      <ProSidebarProvider>
        <AppRoutes />
      </ProSidebarProvider>
    </Alexandria>
  );
};

run();
